import { useState } from 'react';
import { useRouter } from 'next/router';

import { Box, FormField, Heading, Label, Paragraph } from '@zillow/constellation-10';

import { useGetListingDetails, useGetTours } from '../../../../hooks/queries/properties';
import accessUtils from '../../../../utils/accessUtils';
import propertyUtils from '../../../../utils/propertyUtils';

import SFTotalMonthlyPriceWithFeeAcknowledgementModal from '../../../modals/SFTotalMonthlyPriceWithFeeAcknowledgementModal';

import {
  wizardFlexContainerStyles,
  wizardFormSectionContainerStyles,
  wizardHeadingStyles,
  wizardParagraphStyles,
} from '../../../../styles/wizard';

import SFCostAndFeesTotalMonthlyPriceWithFeeAlert from '../../../Alerts/SFCostAndFeesTotalMonthlyPriceWithFeeAlert';
import AllowContactByPhoneFormField from '../../../PostingPath/AllowContactByPhoneFormField';
import AmenitiesWithAdditionalAmenitiesFields from '../../../PostingPath/AmenitiesWithAdditionalAmenitiesFields';
import AcceptZillowApplicationsFormField from '../../../PostingPath/ApplicationsFormField/AcceptZillowApplicationsFormField';
import CostsAndFeesFormField from '../../../PostingPath/CostsAndFeesFormField';
import HideAddressFormField from '../../../PostingPath/HideAddressFormField';
import ListingTourFormField from '../../../PostingPath/MediaContent/ListingTourFormField';
import PhotoUploadFormField from '../../../PostingPath/MediaContent/PhotoUploadFormField';
import PetPolicyFormField from '../../../PostingPath/PetPolicyFormField';
import SquareFootageFormField from '../../../PostingPath/SquareFootageFormField';
import SFCostAndFeeOptInTotalMonthlyPriceWithFeeAlert from '../../../SFCostAndFeeOptInTotalMonthlyPriceWithFeeAlert';
import PageWizard from '../../../Wizard/PageWizard';

import type { WizardBodyTemplateProps } from '../../../WizardStepController';

const RelistAdditionalDetails = ({
  onGoToNextStep,
  onGoToPrevStep,
  onExitWizard,
  wizardTitle,
  progressStepper,
  totalSteps,
  currentStepIndex,
  showDesktopViewBreakpoint,
  ...rest
}: WizardBodyTemplateProps) => {
  const router = useRouter();
  const listingAlias = router.query.listingAlias as string;

  const { data: listingDetails } = useGetListingDetails(listingAlias);
  const { vrModels, externalTours } = useGetTours(listingAlias, listingDetails);

  const [showAcknowledgementModal, setShowAcknowledgementModal] = useState(false);

  const isFeed = listingDetails?.isFeed ?? false;
  const isMultifamily = listingDetails?.isMultifamily ?? false;
  const isRoomForRent = propertyUtils.isRoomForRent({ listing: listingDetails });
  const squareFeet = listingDetails?.floorPlans?.[0]?.squareFeet;
  const hidePhone = listingDetails?.hidePhone ?? false;
  const isHidePhoneRestricted = listingDetails?.restrictions?.isHidePhoneRestricted ?? false;
  const isApplicationsUpdateRestricted = listingDetails?.restrictions?.applications?.isUpdateRestricted ?? false;
  const isOptInTotalMonthlyPriceWithFeeEligible = listingDetails?.totalMonthlyPriceWithFee?.isOptInEligible ?? false;
  const isTotalMonthlyPriceWithFeeEligible = listingDetails?.totalMonthlyPriceWithFeeNotice?.isEligible ?? false;

  const canAccessSFCostsAndFees = accessUtils.canAccessSFCostsAndFees({ isFeed, isMultifamily });

  const canAccessTotalMonthlyPriceWithFee = accessUtils.canAccessTotalMonthlyPriceWithFee({
    isEligible: isTotalMonthlyPriceWithFeeEligible,
  });

  const hasSelectedPropertyAmenity = listingDetails?.amenities?.property?.some((group) =>
    group.values?.some((v) => v.isSelected),
  );

  const hasAdditionalAmenities = (listingDetails?.amenities?.additional?.length ?? 0) > 0;

  const renderSquareFootageField = !squareFeet;
  const renderPetsField = propertyUtils.getIsPetsAllowedEmpty(listingDetails?.petsAllowed);
  const renderPhotosField = (listingDetails?.propertyPhotos?.length ?? 0) === 0;
  const renderToursField = vrModels.length + externalTours.length === 0;
  const renderAmenitiesField = !hasSelectedPropertyAmenity && !hasAdditionalAmenities;
  const renderHidePropertyAddressField = listingDetails?.hideStreetAddress === true;
  const renderAllowContactByPhoneField = hidePhone === true && !isHidePhoneRestricted;
  const renderAcceptApplicationsField = !isApplicationsUpdateRestricted && !listingDetails?.isApplicationsAccepted;
  const renderCostsAndFeesField = canAccessSFCostsAndFees;

  const displayTotalMonthlyPriceWithFee = listingDetails?.displayTotalMonthlyPriceWithFee ?? false;

  const handleNextClick = () => {
    if (canAccessTotalMonthlyPriceWithFee && !displayTotalMonthlyPriceWithFee) {
      setShowAcknowledgementModal(true);
      return;
    }
    onGoToNextStep?.();
  };

  return (
    <PageWizard
      {...rest}
      title=""
      wizardTitle={wizardTitle}
      progressStepper={progressStepper}
      totalSteps={totalSteps}
      currentStepIndex={currentStepIndex}
      onNextClick={handleNextClick}
      onBackClick={onGoToPrevStep || undefined}
      onExitTriggered={onExitWizard}
      showDesktopViewBreakpoint={showDesktopViewBreakpoint}
      hideBackButton={false}
    >
      <div data-testid="RelistAdditionalDetails">
        <Box css={wizardFlexContainerStyles}>
          <Box css={wizardFormSectionContainerStyles.raw()}>
            <Heading level={2} textStyle="heading-sm" css={wizardHeadingStyles}>
              Consider adding additional details
            </Heading>
            <Paragraph css={wizardParagraphStyles}>
              Review and update any information that has changed since you last listed this property.
            </Paragraph>

            {renderSquareFootageField && (
              <Box css={{ marginTop: { base: '600', lg: '800' }, marginBottom: '100' }}>
                <SquareFootageFormField isRelistingFieldset={true} />
              </Box>
            )}

            {renderPetsField && (
              <Box css={{ marginTop: { base: '600', lg: '800' }, marginBottom: '100' }}>
                <PetPolicyFormField isRelistingFieldset={true} />
              </Box>
            )}

            {renderPhotosField && (
              <Box css={{ marginTop: { base: '600', lg: '800' }, marginBottom: '100' }}>
                <FormField
                  label={<Label size="lg">Photos</Label>}
                  control={<PhotoUploadFormField />}
                  css={{ marginBottom: '100' }}
                />
              </Box>
            )}

            {renderToursField && !isRoomForRent && (
              <Box css={{ marginTop: { base: '600', lg: '800' }, marginBottom: '100' }}>
                <FormField
                  label={<Label size="lg">3D tour</Label>}
                  control={<ListingTourFormField isRelistingFieldset={true} />}
                  css={{ marginBottom: '100' }}
                />
              </Box>
            )}

            {renderAmenitiesField && (
              <Box css={{ marginTop: { base: '600', lg: '800' }, marginBottom: '100' }}>
                <Box css={{ paddingBottom: '100' }}>
                  <Paragraph textStyle="body-bold" css={{ marginBottom: '100' }}>
                    Amenities
                  </Paragraph>
                  <Paragraph>Sharing more will help renters see themselves in your home.</Paragraph>
                </Box>
                <AmenitiesWithAdditionalAmenitiesFields isRelistingFieldset={true} />
              </Box>
            )}

            {renderHidePropertyAddressField && (
              <Box css={{ marginTop: { base: '600', lg: '800' }, marginBottom: '100' }}>
                <HideAddressFormField isRelistingFieldset={true} />
              </Box>
            )}

            {renderAllowContactByPhoneField && (
              <Box css={{ marginTop: { base: '600', lg: '800' }, marginBottom: '100' }}>
                <AllowContactByPhoneFormField isRelistingFieldset={true} />
              </Box>
            )}

            {renderAcceptApplicationsField && (
              <Box css={{ marginTop: { base: '600', lg: '800' }, marginBottom: '100' }}>
                <AcceptZillowApplicationsFormField />
              </Box>
            )}

            {renderCostsAndFeesField && (
              <Box css={{ marginTop: { base: '600', lg: '800' } }}>
                <Paragraph textStyle="body-bold" css={{ marginBottom: '100' }}>
                  What additional costs and fees do you charge?
                </Paragraph>
                <Box css={{ marginTop: '100', marginBottom: '200' }}>
                  <Paragraph>
                    {isOptInTotalMonthlyPriceWithFeeEligible ? (
                      <>
                        These details help renters know the real cost of renting your property, and helps you meet
                        relevant fee transparency laws.
                      </>
                    ) : (
                      <>
                        We’ve highlighted some of the most common fee{' '}
                        {canAccessTotalMonthlyPriceWithFee ? 'types' : 'categories'}. These details help renters know
                        the real cost of renting your property before they apply.
                      </>
                    )}
                  </Paragraph>
                </Box>

                {canAccessTotalMonthlyPriceWithFee && <SFCostAndFeesTotalMonthlyPriceWithFeeAlert />}
                {isOptInTotalMonthlyPriceWithFeeEligible && (
                  <SFCostAndFeeOptInTotalMonthlyPriceWithFeeAlert triggerLocation="posting_path" />
                )}

                <CostsAndFeesFormField displayedLocation="relisting" />
              </Box>
            )}
          </Box>
        </Box>
      </div>

      {showAcknowledgementModal && (
        <SFTotalMonthlyPriceWithFeeAcknowledgementModal
          onClose={() => setShowAcknowledgementModal(false)}
          onSubmit={() => {
            setShowAcknowledgementModal(false);
            onGoToNextStep?.();
          }}
        />
      )}
    </PageWizard>
  );
};

export default RelistAdditionalDetails;
